import { z } from 'zod';
import { LIMITS } from '../config/timing.js';
import { tourSettingsSchema } from '../config/thresholds.js';
import { navigationRouteSchema } from '../navigation/types.js';
import { convoyStateSchema, idSchema, latLonSchema, latSchema, lonSchema, pageVisibilitySchema, participantRoleSchema, participantSchema, positionSampleSchema, statusMessageKindSchema, statusMessageSchema, timestampSchema, tourSchema, trackPointSchema, trackSegmentSchema, vehicleStateSchema, waypointSchema, } from '../domain/types.js';
/**
 * Das Echtzeitprotokoll.
 *
 * Diese Datei ist die verbindliche Schnittstelle zwischen Backend,
 * Weboberfläche und Simulator — und die Grundlage für einen späteren
 * Flutter-Client, der dasselbe Backend benutzt. Beschrieben ist sie in
 * docs/api.md.
 *
 * Jede Nachricht wird zur Laufzeit geprüft, in beide Richtungen. Der Simulator
 * kann dadurch nichts senden, was ein echter Client nicht senden könnte.
 */
/** Ein zusammenhängendes Stück Führungsspur. */
export const trackChunkSchema = z.object({
    segmentId: idSchema,
    points: z.array(trackPointSchema),
});
// ===========================================================================
// Vom Client zum Server
// ===========================================================================
/**
 * Anmeldung an der Verbindung. Teilnehmer und Sitzungstoken stammen aus dem
 * vorangegangenen Beitritt über die HTTP-Schnittstelle.
 */
export const joinMessageSchema = z.object({
    type: z.literal('join'),
    tourId: idSchema,
    participantId: idSchema,
    sessionToken: z.string().min(1),
    /**
     * Höchste bereits bekannte Nummer eines Spurpunkts. Der Server schickt nur,
     * was darüber liegt. Beim ersten Verbinden nicht gesetzt.
     */
    sinceTrackSeq: z.number().int().nonnegative().optional(),
});
export const positionMessageSchema = z.object({
    type: z.literal('position'),
    position: positionSampleSchema,
});
/** Nachsenden aus dem Puffer, nachdem ein Funkloch durchfahren wurde. */
export const positionsBatchMessageSchema = z.object({
    type: z.literal('positions_batch'),
    positions: z.array(positionSampleSchema).min(1).max(LIMITS.positionsPerBatch),
});
export const statusMessageOutSchema = z.object({
    type: z.literal('status_message'),
    kind: statusMessageKindSchema,
    lat: latSchema.optional(),
    lon: lonSchema.optional(),
    /**
     * Wann der Fahrer getippt hat — nicht, wann die Nachricht ankam.
     *
     * Eine Meldung, die im Funkloch abgesetzt wurde, wird gepuffert und später
     * zugestellt. Ohne diese Angabe erschiene eine Panne von vor zwanzig Minuten
     * als „gerade eben"; Abschnitt 10.1 des Auftrags lässt dazu keinen
     * Spielraum, und für eine Statusmeldung gilt nichts anderes als für eine
     * Position.
     *
     * **Wahlfrei, damit ein Ausrollen niemandem die Panne zerschlägt.** Ein
     * Browser, der noch das alte Bündel geladen hat, sendet das Feld nicht — der
     * Server nimmt dann den Zeitpunkt des Eingangs. Ein Pflichtfeld hätte
     * genau die Meldung abgewiesen, um die es hier geht.
     */
    recordedAt: timestampSchema.optional(),
});
/** Rollenwechsel, insbesondere die Übergabe der Führung. */
export const roleChangeMessageSchema = z.object({
    type: z.literal('role_change'),
    participantId: idSchema,
    role: participantRoleSchema,
});
/**
 * Das Führungsfahrzeug stößt für ein abgehängtes Fahrzeug eine Navigation an.
 * Auf dessen Gerät erscheint ein Hinweis mit einer einzigen Schaltfläche; die
 * Navigation startet niemals von selbst.
 */
export const requestNavigationMessageSchema = z.object({
    type: z.literal('request_navigation'),
    participantId: idSchema,
});
/**
 * Die Navigation für sich selbst starten — Stufe 2.
 *
 * Zwei Wege führen hierher, und beide enden bei einem Fingertipp des Fahrers:
 * Er hat den Hinweis der Führung angenommen, oder er hat selbst gemerkt, dass
 * er falsch ist, und „Zurück zur Kolonne" gedrückt. Von selbst startet nichts.
 *
 * Der Server antwortet mit `navigation_route` und rechnet die Route danach im
 * Takt von `ROUTING.recalcIntervalMs` neu, weil das Ziel weiterfährt.
 */
export const navigationStartMessageSchema = z.object({
    type: z.literal('navigation_start'),
});
/** Die Navigation beenden. Der Fahrer entscheidet, wann er sie nicht mehr braucht. */
export const navigationStopMessageSchema = z.object({
    type: z.literal('navigation_stop'),
});
/**
 * Sichtbarkeit der Seite. Geht die App in den Hintergrund, endet die
 * Positionserfassung im Browser — das ist nicht umgehbar. Der Client meldet es
 * unmittelbar, damit das Fahrzeug bei allen anderen als pausiert erscheint und
 * nicht mit einer veralteten Position weiterlebt.
 *
 * Nicht zu verwechseln mit dem Unsichtbarschalten aus Datenschutzgründen: das
 * ist eine dauerhafte Einstellung des Teilnehmers und läuft über die
 * HTTP-Schnittstelle.
 */
export const visibilityMessageSchema = z.object({
    type: z.literal('visibility'),
    state: pageVisibilitySchema,
});
/**
 * Der Empfang einer Meldung wird zurückgemeldet.
 *
 * Nur für Meldungen der Führung vorgesehen (siehe STATUS_MESSAGE_RULES). Wer
 * „Wir halten an" schickt, muss sehen, wer es gelesen hat — und wer nicht.
 * Der Server schickt die Meldung danach mit ergänztem `acknowledgedBy` erneut
 * an alle, die sie ohnehin bekommen haben.
 */
export const acknowledgeStatusMessageSchema = z.object({
    type: z.literal('acknowledge_status'),
    messageId: idSchema,
});
/**
 * Die Ausfahrt verlassen.
 *
 * `successorId` ist Pflicht, wenn das Führungsfahrzeug geht und noch jemand
 * dabei ist: Die Führung wird übergeben, nicht niedergelegt. Ohne Angabe
 * antwortet der Server mit `leader_must_hand_over`. Für alle anderen bleibt das
 * Feld leer — siehe `planLeave` in `domain/leaving.ts`.
 */
export const leaveMessageSchema = z.object({
    type: z.literal('leave'),
    successorId: idSchema.optional(),
});
/**
 * Das Führungsfahrzeug nimmt ein Fahrzeug aus der Kolonne.
 *
 * Gebraucht für den Fall, dass jemand nicht mehr mitfährt, aber vergessen hat,
 * sich abzumelden — sonst zählte er weiter mit, stünde in jeder Liste und
 * bekäme Warnungen über sich selbst.
 *
 * Nur die Führung darf das, und sie kann sich nicht selbst entfernen: Eine
 * Ausfahrt ohne Führung hätte keine Spur und damit keinen Rückstand.
 */
export const removeParticipantMessageSchema = z.object({
    type: z.literal('remove_participant'),
    participantId: idSchema,
});
export const pingMessageSchema = z.object({
    type: z.literal('ping'),
    sentAt: timestampSchema,
});
/**
 * Was das Gerät über seine eigene Verbindung weiß (A-139).
 *
 * - `network_offline`, `network_online` — der Browser meldet, dass das Gerät
 *   kein Netz mehr hat oder wieder eines
 * - `heartbeat_gave_up` — der Herzschlag hat die Verbindung für tot erklärt
 * - `join_unanswered` — auf eine Anmeldung kam binnen
 *   `joinConfirmTimeoutMs` keine Antwort
 */
export const deviceEventKindSchema = z.enum([
    'network_offline',
    'network_online',
    'heartbeat_gave_up',
    'join_unanswered',
]);
/**
 * Eine Gerätemeldung — nur zum Mitschreiben (A-139).
 *
 * Der Server schreibt sie ins Protokoll und tut sonst nichts damit. Sie
 * beantwortet eine Frage, die sich sonst nicht beantworten lässt: Meldet der
 * Browser ein Funkloch früher, als der Herzschlag es bemerkt?
 *
 * `at` ist der Zeitpunkt auf dem Gerät. Meistens geschieht, was gemeldet wird,
 * ohne Verbindung — die Meldung kommt deshalb nach dem Wiederverbinden, und
 * erst `at` sagt, wann.
 */
export const deviceEventMessageSchema = z.object({
    type: z.literal('device_event'),
    kind: deviceEventKindSchema,
    at: timestampSchema,
});
export const clientMessageSchema = z.discriminatedUnion('type', [
    joinMessageSchema,
    positionMessageSchema,
    positionsBatchMessageSchema,
    statusMessageOutSchema,
    acknowledgeStatusMessageSchema,
    removeParticipantMessageSchema,
    roleChangeMessageSchema,
    requestNavigationMessageSchema,
    navigationStartMessageSchema,
    navigationStopMessageSchema,
    visibilityMessageSchema,
    leaveMessageSchema,
    pingMessageSchema,
    deviceEventMessageSchema,
]);
// ===========================================================================
// Vom Server zum Client
// ===========================================================================
/**
 * Vollständiger Zustand. Wird nach dem Verbindungsaufbau geschickt und immer
 * dann erneut, wenn sich etwas Strukturelles ändert — Teilnehmer kommt hinzu
 * oder geht, Rolle wechselt, Ausfahrt wird pausiert, Schwellwerte geändert.
 * Laufende Bewegung läuft dagegen ausschließlich über `positions`.
 */
export const tourStateMessageSchema = z.object({
    type: z.literal('tour_state'),
    at: timestampSchema,
    tour: tourSchema,
    settings: tourSettingsSchema,
    /** Der eigene Teilnehmer, damit der Client sich in der Liste findet. */
    youId: idSchema,
    participants: z.array(participantSchema),
    vehicles: z.array(vehicleStateSchema),
    segments: z.array(trackSegmentSchema),
    /** Nur der noch nicht bekannte Teil der Spur. */
    track: z.array(trackChunkSchema),
    /** Ist die Spur damit noch nicht vollständig, folgen leader_track_update-Nachrichten. */
    trackComplete: z.boolean(),
    statusMessages: z.array(statusMessageSchema),
});
/** Gebündelte Bewegung, höchstens einmal je Sekunde. */
export const positionsMessageSchema = z.object({
    type: z.literal('positions'),
    at: timestampSchema,
    vehicles: z.array(vehicleStateSchema),
});
/** Ausschließlich der Zuwachs der Führungsspur, niemals die ganze Spur. */
export const leaderTrackUpdateMessageSchema = z.object({
    type: z.literal('leader_track_update'),
    at: timestampSchema,
    /** Beginnt ein neuer Abschnitt, steht er hier — die Linie darf nicht durchgezogen werden. */
    segments: z.array(trackSegmentSchema),
    track: z.array(trackChunkSchema),
});
/**
 * Ein Fahrzeug hat den Anschluss verloren. Wird je Ereignis genau einmal
 * geschickt und erst nach Rückkehr in den Anschluss zurückgesetzt.
 */
export const lostContactMessageSchema = z.object({
    type: z.literal('lost_contact'),
    at: timestampSchema,
    participantId: idSchema,
    convoyState: convoyStateSchema,
    delaySeconds: z.number().nullable(),
});
export const contactRestoredMessageSchema = z.object({
    type: z.literal('contact_restored'),
    at: timestampSchema,
    participantId: idSchema,
});
/** Auch eine Quittung kommt als dieselbe Nachricht, mit ergänztem acknowledgedBy. */
export const statusMessageInSchema = z.object({
    type: z.literal('status_message'),
    message: statusMessageSchema,
});
export const roleChangedMessageSchema = z.object({
    type: z.literal('role_changed'),
    at: timestampSchema,
    participantId: idSchema,
    role: participantRoleSchema,
    /** Wer den Wechsel ausgelöst hat. */
    byParticipantId: idSchema.nullable(),
});
export const navigationRequestedMessageSchema = z.object({
    type: z.literal('navigation_requested'),
    at: timestampSchema,
    targetParticipantId: idSchema,
    byParticipantId: idSchema,
});
/**
 * Die berechnete Navigation — Stufe 2.
 *
 * Geht nur an das Fahrzeug, für das sie gilt. Sie wird im Takt neu geschickt,
 * weil die Führung weiterfährt; der Client ersetzt seine Route dann vollständig.
 * Wo der Fahrer auf ihr steht, rechnet er selbst aus — dafür braucht es keine
 * Nachfrage beim Server.
 */
export const navigationRouteMessageSchema = z.object({
    type: z.literal('navigation_route'),
    at: timestampSchema,
    route: navigationRouteSchema,
});
/**
 * Warum eine Navigation endet.
 *
 * `arrived` — die Kolonne ist erreicht.
 * `stopped` — der Fahrer hat sie beendet.
 * `unavailable` — der Routingdienst ist nicht erreichbar oder nicht eingerichtet.
 * `no_route` — es gibt keinen Weg dorthin, etwa weil die Führung noch keine
 *   Position gemeldet hat.
 */
export const navigationEndReasonSchema = z.enum([
    'arrived',
    'stopped',
    'unavailable',
    'no_route',
]);
export const navigationEndedMessageSchema = z.object({
    type: z.literal('navigation_ended'),
    at: timestampSchema,
    reason: navigationEndReasonSchema,
});
export const pongMessageSchema = z.object({
    type: z.literal('pong'),
    /** Der Zeitpunkt aus dem ping, unverändert zurück. */
    sentAt: timestampSchema,
    /**
     * Uhrzeit des Servers. Der Client kann daraus den Gang seiner eigenen Uhr
     * abgleichen — sonst zeigt ein Gerät mit falsch gehender Uhr einen falschen
     * Rückstand an.
     */
    serverTime: timestampSchema,
});
export const protocolErrorCodeSchema = z.enum([
    /** Sitzungstoken fehlt, ist falsch oder gehört nicht zu dieser Ausfahrt. */
    'unauthorized',
    /**
     * Der Teilnehmer gehört nicht mehr zu dieser Ausfahrt — er hat sie selbst
     * verlassen oder das Führungsfahrzeug hat ihn herausgenommen.
     *
     * Eigener Code, nicht `unauthorized`: Ein Client muss das unterscheiden
     * können. Bei `unauthorized` lohnt ein erneuter Versuch, hier nicht. Wer das
     * verwechselt, lässt ein Gerät bis zum Ende der Fahrt gegen eine Ausfahrt
     * anlaufen, zu der es nicht mehr gehört.
     */
    'removed',
    'tour_not_found',
    /** Die Ausfahrt ist beendet; eine Verbindung ist nicht mehr möglich. */
    'tour_finished',
    /** Die Nachricht war nicht lesbar oder entsprach nicht dem Protokoll. */
    'invalid_message',
    /**
     * Das Führungsfahrzeug will die Ausfahrt verlassen, ohne die Führung zu
     * übergeben. Ein Client fragt daraufhin, an wen — und schickt `leave` erneut,
     * diesmal mit `successorId`.
     */
    'leader_must_hand_over',
    'internal',
]);
/**
 * Fehlermeldung. Im Auftrag nicht aufgeführt, aber unverzichtbar: ohne sie
 * bliebe einem Client bei einem abgelehnten Beitritt nur eine grundlos
 * geschlossene Verbindung, und dem Fahrer eine Anwendung, die schweigt.
 */
export const errorMessageSchema = z.object({
    type: z.literal('error'),
    code: protocolErrorCodeSchema,
    /** Für den Fahrer verständlicher deutscher Text. */
    message: z.string(),
});
/**
 * Die geplante Route der Ausfahrt (Stufe 3).
 *
 * ## Warum eine eigene Nachricht und nicht ein Feld in `tour_state`
 *
 * `tour_state` geht bei **jeder** strukturellen Änderung neu an alle — ein
 * Beitritt, ein Rollenwechsel, geänderte Schwellwerte. Eine Route mit
 * zwanzigtausend Punkten läge damit bei jedem dieser Anlässe erneut auf der
 * Leitung, und das an Geräten, die am Rand eines Funklochs fahren.
 *
 * Die Route ändert sich selten und dann vollständig. Sie kommt deshalb einmal
 * beim Beitritt und danach nur, wenn sie sich wirklich geändert hat.
 *
 * ## Warum als Ganzes und nicht als Zuwachs
 *
 * Die Führungsspur wächst während der Fahrt, Punkt für Punkt — dort wäre alles
 * andere als ein Zuwachs Verschwendung. Eine geplante Route wächst nicht; sie
 * wird ersetzt. Ein Zuwachs hätte nichts, woran er anknüpfen könnte.
 *
 * `route: null` heißt ausdrücklich „für diese Ausfahrt ist nichts geplant" —
 * und nicht „ich weiß es nicht". Ohne diese Unterscheidung wüsste der Client
 * beim Beitritt nie, ob er noch warten soll.
 */
export const routePlanMessageSchema = z.object({
    type: z.literal('route_plan'),
    route: z
        .object({
        id: idSchema,
        name: z.string(),
        /** `gpx` = eingelesene Datei, `planner` = in der Anwendung geplant. */
        source: z.enum(['gpx', 'planner']),
        distanceM: z.number().nonnegative(),
        /** Geschätzte Fahrzeit, sofern die Datei eine nennt. */
        durationS: z.number().int().nonnegative().nullable(),
        points: z.array(latLonSchema),
    })
        .nullable(),
    /**
     * Die Treffpunkte — **auch ohne Route**.
     *
     * Sie hängen an der Ausfahrt, nicht an der Strecke. Wer eine spontane Runde
     * fährt und nur „wir treffen uns am Marktplatz" festhält, hat Treffpunkte und
     * keine Route; stünden sie im `route`-Objekt, wären sie dann nicht
     * übertragbar. `distanceM` ist in diesem Fall `null` — die Reihenfolge ist
     * dann die der Eingabe, mehr ist nicht bekannt.
     *
     * `shaping` ist hier **nicht** enthalten: Stützpunkte formen die Route und
     * sind für den Fahrer unsichtbar. Sie über die Leitung zu schicken, damit
     * jedes Gerät sie wieder wegfiltert, wäre Verschwendung an genau den
     * Geräten, die am Rand eines Funklochs fahren.
     */
    waypoints: z.array(waypointSchema),
});
/**
 * Wer an welchem Treffpunkt angekommen ist (Stufe 3, Schritt 3).
 *
 * ## Warum als Ganzes und nicht als Zuwachs
 *
 * Die Liste ist klein: Teilnehmer mal Treffpunkte, in der Praxis ein paar
 * Dutzend Einträge von je drei Feldern. Ein Zuwachs bräuchte einen Stand, den
 * beide Seiten führen — für Daten, die in eine einzige Nachricht passen. Die
 * Führungsspur wächst während der Fahrt unaufhörlich; diese Liste wächst
 * höchstens um einen Eintrag, wenn jemand irgendwo ankommt.
 *
 * ## Warum sie an alle geht und nicht nur an die Führung
 *
 * „Wer ist schon da" ist die Frage der Führung am Sammelpunkt — aber wer
 * zurückliegt, will ebenso wissen, ob die anderen schon warten. Personenbezug
 * entsteht dabei keiner, den es nicht ohnehin gäbe: Wer in derselben Ausfahrt
 * ist, sieht die Position der anderen ohnehin auf der Karte.
 *
 * Kommt beim Beitritt und danach, sooft jemand ankommt.
 */
export const waypointArrivalsMessageSchema = z.object({
    type: z.literal('waypoint_arrivals'),
    arrivals: z.array(z.object({
        waypointId: idSchema,
        participantId: idSchema,
        arrivedAt: timestampSchema,
    })),
});
export const serverMessageSchema = z.discriminatedUnion('type', [
    tourStateMessageSchema,
    positionsMessageSchema,
    leaderTrackUpdateMessageSchema,
    lostContactMessageSchema,
    contactRestoredMessageSchema,
    statusMessageInSchema,
    roleChangedMessageSchema,
    navigationRequestedMessageSchema,
    navigationRouteMessageSchema,
    navigationEndedMessageSchema,
    routePlanMessageSchema,
    waypointArrivalsMessageSchema,
    pongMessageSchema,
    errorMessageSchema,
]);
function decode(raw, schema) {
    let parsed;
    try {
        parsed = JSON.parse(raw);
    }
    catch {
        return { ok: false, reason: 'Die Nachricht ist kein gültiges JSON.' };
    }
    const result = schema.safeParse(parsed);
    if (!result.success) {
        const first = result.error.issues[0];
        const where = first === undefined ? '' : ` bei "${first.path.join('.')}": ${first.message}`;
        return { ok: false, reason: `Die Nachricht entspricht nicht dem Protokoll${where}` };
    }
    return { ok: true, message: result.data };
}
export function decodeClientMessage(raw) {
    return decode(raw, clientMessageSchema);
}
export function decodeServerMessage(raw) {
    return decode(raw, serverMessageSchema);
}
export function encodeClientMessage(message) {
    return JSON.stringify(message);
}
export function encodeServerMessage(message) {
    return JSON.stringify(message);
}
/**
 * Kürzt eine Koordinate auf die übertragene Genauigkeit. Sechs Nachkommastellen
 * sind etwa elf Zentimeter; alles Weitere ist Rauschen und kostet nur Bandbreite.
 */
export function roundCoordinate(value) {
    const factor = 10 ** LIMITS.coordinateDecimals;
    return Math.round(value * factor) / factor;
}
//# sourceMappingURL=messages.js.map